import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import salesApiClient from '@/api/salesClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogOverlay } from '@/components/ui/dialog';
import { Plus, Mail, Phone, Building2, Trash2, Users, LayoutGrid, List, Search } from 'lucide-react';

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    fetchContacts();
    fetchUsers();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await salesApiClient.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await salesApiClient.get('/team/members');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const getFilteredContacts = () => {
    return contacts.filter(contact => {
      const matchesSearch = searchTerm === '' || 
        contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesUser = filterUser === '' || contact.created_by === filterUser;
      
      // Date filtering
      let matchesDate = true;
      if (filterDateFrom || filterDateTo) {
        const contactDate = new Date(contact.created_at);
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          matchesDate = matchesDate && contactDate >= fromDate;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && contactDate <= toDate;
        }
      }
      
      return matchesSearch && matchesUser && matchesDate;
    });
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await salesApiClient.post('/contacts', formData);
      toast.success('Contact created successfully');
      setIsDialogOpen(false);
      setFormData({ name: '', title: '', company: '', email: '', phone: '' });
      fetchContacts();
    } catch (error) {
      toast.error('Failed to create contact');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await salesApiClient.delete(`/contacts/${id}`);
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="contacts-title">
            Contacts
          </h1>
          <p className="text-base text-slate-400 mt-2">Manage your CTO and IT head contacts</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-contact-button">
              <Plus size={20} className="mr-2" />
              Add Contact
            </Button>
          </DialogTrigger>
          <DialogOverlay className="bg-black/80" />
          <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="add-contact-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Contact</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-200">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="contact-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title" className="text-slate-200">Job Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="CTO"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="contact-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-slate-200">Company</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Tech Corp"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="contact-company-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@techcorp.com"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="contact-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-200">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="contact-phone-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-contact-button">
                Create Contact
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input 
            placeholder="Search contacts..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          className="h-10 rounded-md border border-slate-700 bg-slate-800 text-white px-3 text-sm"
        >
          <option value="">All Sales Reps</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">From:</span>
          <Input 
            type="date" 
            value={filterDateFrom} 
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">To:</span>
          <Input 
            type="date" 
            value={filterDateTo} 
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        {(filterUser || filterDateFrom || filterDateTo) && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setFilterUser('');
              setFilterDateFrom('');
              setFilterDateTo('');
            }}
            className="text-slate-400 hover:text-white"
          >
            Clear Filters
          </Button>
        )}
        <div className="flex bg-slate-800 rounded-lg p-1">
          <Button size="sm" variant={viewMode === 'grid' ? 'default' : 'ghost'} onClick={() => setViewMode('grid')}>
            <LayoutGrid size={18} />
          </Button>
          <Button size="sm" variant={viewMode === 'list' ? 'default' : 'ghost'} onClick={() => setViewMode('list')}>
            <List size={18} />
          </Button>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="contacts-grid">
        {getFilteredContacts().map((contact) => (
          <Card
            key={contact.id}
            className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm hover:shadow-md hover:border-slate-700 transition-all duration-200 p-6"
            data-testid={`contact-card-${contact.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-white" data-testid="contact-name">{contact.name}</h3>
                <p className="text-sm text-slate-400" data-testid="contact-title">{contact.title}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDelete(contact.id)}
                className="hover:bg-red-500/10"
                data-testid="delete-contact-button"
              >
                <Trash2 size={18} className="text-red-400" />
              </Button>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Building2 size={16} />
                <span data-testid="contact-company">{contact.company}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Mail size={16} />
                <span data-testid="contact-email">{contact.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Phone size={16} />
                <span data-testid="contact-phone">{contact.phone}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-cyan-400 mt-2">
                <Users size={14} />
                <span>{getUserName(contact.created_by)}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="bg-slate-900 border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Sales Rep</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {getFilteredContacts().map(contact => (
                <tr key={contact.id} className="hover:bg-slate-800/50">
                  <td className="px-4 py-3 text-white font-medium">{contact.name}</td>
                  <td className="px-4 py-3 text-slate-400">{contact.title}</td>
                  <td className="px-4 py-3 text-slate-300">{contact.company}</td>
                  <td className="px-4 py-3 text-slate-400">{contact.email}</td>
                  <td className="px-4 py-3 text-slate-400">{contact.phone}</td>
                  <td className="px-4 py-3 text-cyan-400">{getUserName(contact.created_by)}</td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(contact.id)} className="hover:bg-red-500/10">
                      <Trash2 size={16} className="text-red-400" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {getFilteredContacts().length === 0 && (
            <div className="p-8 text-center text-slate-400">No contacts found</div>
          )}
        </Card>
      )}

      {contacts.length === 0 && viewMode === 'grid' && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-12 text-center" data-testid="no-contacts">
          <Users size={48} className="mx-auto text-slate-700 mb-4" />
          <p className="text-slate-400">No contacts yet. Add your first contact to get started.</p>
        </Card>
      )}
    </div>
  );
}
